import {
  BOOKING_MAPPING,
  BOOKING_PAY_AT_HOTEL_MAPPING,
  PAYMENT_METHOD,
  DEFAULT_LAYOUT_STATUS,
  AmenityIconsMap,
  BOOKING_PAY_LATER_MAPPING,
} from '@airasia-common/libraries/constants';
import { handleActions } from 'redux-actions';
import { countNights } from '@airasia-common/libraries/datetime';
import {
  HotelBookingSchema,
  HotelContentSchema,
} from '@airasia-common/libraries/validation/schemas';
import {
  CHARGE_TYPE,
  DISCOUNT_METHOD,
} from '@airasia-common/libraries/constants/Payment';
import {
  getAmenityTranslationKey,
  AMENITY_ICON_TYPE,
} from '@airasia-common/libraries/utilities/AmenityHelper';
import { getHeroImage } from '../../helpers';
import { THUMBNAIL_SIZE } from '../../constants';
import { types } from './booking.meta';
import {
  parseCancellationDate,
  isActivePenalty,
  filterPenalties,
  getRefundType,
} from './cancellation';

const initialState = {
  cancellation: {
    fetching: false,
    failed: false,
    showSuccessMessage: false,
    showFailedMessage: false,
    rate: {},
    hasCompletedPrecancellation: false,
  },
  cancellationDate: '',
  isFetching: false,
  isFailed: false,
  layout: {},
};

export function fetchBooking(state, action) {
  return {
    ...state,
    isFetching: true,
    isFailed: false,
    key: action.payload,
    cancelPenalties: [],
    cancellation: { ...initialState.cancellation },
  };
}

export function reloadBooking(state) {
  return {
    ...state,
    isFetching: true,
    isFailed: false,
    cancelPenalties: [],
    cancellation: { ...state.cancellation },
  };
}

const mapBookingLinks = links => {
  const {
    pre_cancel: preCancel = {},
    cancel = {},
    send_voucher: sendVoucher = {},
  } = links;

  return {
    preCancel: preCancel.href,
    cancel: cancel.href,
    sendVoucher: sendVoucher.href,
  };
};

export function fetchBookingCompleted(state, action) {
  try {
    HotelBookingSchema.validateSync(action.payload);
    const {
      order_id: orderId,
      order_number: orderNumber,
      hotel_id: hotelId,
      status,
      checkin,
      checkout,
      total_price: totalPrice,
      payable_at_site,
      rooms,
      cancellation_details: cancellationDetails = {},
      cancellation_policy = { refund_amount: cancellationDetails.amount },
      serverTime,
      creation_date_time: creationDateTime,
      billing_contact: billingContact = {},
      links,
      booking_ref_id: bookingId,
      total_occupancy: totalOccupancy,
      payments,
      pay_at_hotel: isPayAtHotel,
      refundable: isFreeCancellation,
      rate_amenities: rateAmenities,
      buy_now_pay_later: isPayLater,
      buy_now_pay_later_date: payLaterDate,
    } = action.payload;
    const [room] = rooms || [];
    const { room_name: roomName } = room || {};
    const totalRooms = (rooms && rooms.length) || 0;
    const totalNights = countNights(checkin, checkout);

    const {
      total_inclusive_price: totalInclusivePrice,
      total_exclusive_price: totalExclusivePrice,
      total_tax: totalTax,
      total_fee: totalFee,
      tax_breakdown,
      fee_breakdown,
      total_discount: totalDiscount,
      discount_breakdown = [],
    } = totalPrice || {};

    const hasValidAmount = price => {
      return price && price.amount && Number(price.amount.value) >= 0;
    };

    const reducePrices = prices => {
      return prices ? prices.filter(hasValidAmount) : [];
    };

    const isTaxIncluded = totalTax ? Number(totalTax.value) === 0 : true;
    const isFeeIncluded = totalFee ? Number(totalFee.value) === 0 : true;

    const taxBreakdown = !isTaxIncluded ? reducePrices(tax_breakdown) : [];
    const feeBreakdown = !isFeeIncluded ? reducePrices(fee_breakdown) : [];
    const payableAtSite = reducePrices(payable_at_site);

    const hotelChargedPropertyFee =
      payable_at_site &&
      payable_at_site.find(item => item.type === CHARGE_TYPE.MANDATORY_FEE);

    const isTaxAndFeeCombined = totalTax
      ? Number(totalTax.value) > 0 &&
        taxBreakdown.find(item => item.type === CHARGE_TYPE.TAXES_AND_FEES)
      : false;
    const { cancellation_date_time } = cancellationDetails;
    const cancellationDate = parseCancellationDate(cancellation_date_time);
    const { penalties = [] } = cancellation_policy;
    const cancelPenalties = filterPenalties(penalties, checkout, serverTime);
    const { last_name, email } = billingContact;
    const paymentDetails = payments || [];
    const loyaltyOrPromoDiscountPayment = paymentDetails.find(
      e =>
        e.payment_method === PAYMENT_METHOD.LOYALTY ||
        e.payment_method === PAYMENT_METHOD.DISCOUNT
    );
    const totalToBePaid = paymentDetails.find(
      e =>
        e.payment_method === PAYMENT_METHOD.CREDIT_CARD ||
        e.payment_method === PAYMENT_METHOD.BNPL ||
        e.payment_method === PAYMENT_METHOD.DIRECT_DEBIT ||
        e.payment_method === PAYMENT_METHOD.BIGPAY
    );
    let cancellationRefundAmount = cancellationDetails.refund_details?.find(
      e =>
        e.payment_method === PAYMENT_METHOD.CREDIT_CARD ||
        e.payment_method === PAYMENT_METHOD.DIRECT_DEBIT
    );
    const amountRefundedDefault = '0.0';
    if (!cancellationRefundAmount) {
      cancellationRefundAmount = {
        amount: {
          currency: cancellationDetails.amount?.currency,
          value: amountRefundedDefault,
        },
      };
    }

    const discountBreakup = discount_breakdown;
    const lobDisount = discountBreakup
      ? discountBreakup.find(
          e => e.source && e.source === DISCOUNT_METHOD.LOB_DISCOUNT
        )
      : null;
    const cardDiscount = discountBreakup
      ? discountBreakup.find(
          e => e.source && e.source === DISCOUNT_METHOD.CARD_DISCOUNT
        )
      : null;

    const layoutsBooking = isPayAtHotel
      ? BOOKING_PAY_AT_HOTEL_MAPPING
      : isPayLater
      ? BOOKING_PAY_LATER_MAPPING
      : BOOKING_MAPPING;
    const layout =
      layoutsBooking[status] || layoutsBooking[DEFAULT_LAYOUT_STATUS];

    return {
      ...state,
      cancellation: {
        ...state.cancellation,
        rate: cancellationRefundAmount.amount,
      },
      payment: {
        totalExclusivePrice,
        totalInclusivePrice,
        totalTax,
        totalFee,
        breakdownPrices: [...taxBreakdown, ...feeBreakdown],
        payableAtSite,
        isTaxAndFeeCombined,
        isTaxIncluded,
        isFeeIncluded,
        hotelChargedPropertyFee,
        loyaltyOrPromoDiscountPayment,
        totalToBePaid,
        totalDiscount,
        lobDisount,
        cardDiscount,
      },
      isFetching: false,
      isFailed: false,
      orderId,
      orderNumber,
      hotelId,
      status,
      checkin,
      checkout,
      roomName,
      totalRooms,
      totalNights,
      totalOccupancy,
      cancellation_policy,
      cancelPenalties,
      cancellationDate,
      layout: layout || {},
      billingContactLastName: last_name,
      billingContactEmail: email,
      creationDateTime: parseCancellationDate(creationDateTime),
      links: mapBookingLinks(links),
      bookingId,
      isPayAtHotel,
      isFreeCancellation,
      amenities: rateAmenities
        ? rateAmenities.map(amenity => {
            return {
              ...amenity,
              displayId: `rate${AmenityIconsMap.split.level}${
                AmenityIconsMap.rate[amenity.id]
              }`,
              translationKey: getAmenityTranslationKey(
                AMENITY_ICON_TYPE.RATE,
                amenity.id
              ),
            };
          })
        : [],
      paymentDetails,
      isPayLater,
      payLaterDate,
      ...action.payload.ssoOriginData,
    };
  } catch (error) {
    console.log('[Discarded Hotel Booking]', action.payload, error);

    return {
      ...state,
      isFetching: false,
      isFailed: true,
    };
  }
}

export function fetchBookingFailed(state) {
  return {
    ...state,
    isFetching: false,
    isFailed: true,
  };
}

export function fetchBookingHotelContentCompleted(state, action) {
  if (state.isFailed) {
    return state;
  }
  const [hotelContentData, roomDetailData] = action.payload;

  try {
    HotelContentSchema.validateSync(hotelContentData);
    const {
      id,
      name,
      gallery,
      address,
      description,
      checkin_details,
      checkout_details,
      policies,
      fees,
      guest_rating,
      star_rating,
      amenities,
      phone = '',
      fax = '',
    } = hotelContentData;
    const image = getHeroImage(gallery.images, THUMBNAIL_SIZE);
    const displayAmenities =
      amenities &&
      amenities.top_amenities &&
      amenities.top_amenities.filter(({ name }) => Boolean(name)).slice(0, 6);

    const hotelContent = {
      id,
      name,
      image,
      address,
      description,
      checkinDetails: checkin_details,
      checkoutDetails: checkout_details,
      policies,
      fees,
      guestRating: guest_rating?.rating ? guest_rating?.rating : '0',
      starRating: star_rating?.rating ? star_rating?.rating : '1.0',
      numberOfReview: guest_rating?.count ? guest_rating?.count : 0,
      displayAmenities,
      phone,
      fax,
    };

    if (!roomDetailData) return state;
    const { room } = roomDetailData;
    const { name: roomName, views_from_room } = room;
    const otherAmenities =
      views_from_room && views_from_room.filter(({ name }) => Boolean(name));

    const roomDetail = { ...room, otherAmenities };
    return {
      ...state,
      isFetching: false,
      isFailed: false,
      hotelContent,
      checkinDetails: checkin_details,
      checkoutDetails: checkout_details,
      policies,
      fees,
      roomDetail,
    };
  } catch (error) {
    console.log('[Discarded Hotel Booking]', action.payload, error);

    return {
      ...state,
      isFetching: false,
      isFailed: true,
    };
  }
}

export function fetchBookingHotelContentFailed(state) {
  return {
    ...state,
    isFetching: false,
    isFailed: true,
  };
}

export const requestPrecancellation = state => ({
  ...state,
  cancellation: {
    ...state.cancellation,
    hasCompletedPrecancellation: false,
    showSuccessMessage: false,
    showFailedMessage: false,
    fetching: true,
    failed: false,
  },
});

export const requestPrecancellationFailed = state => ({
  ...state,
  cancellation: {
    ...state.cancellation,
    hasCompletedPrecancellation: false,
    failed: true,
    fetching: false,
  },
});

const requestPrecancellationCompleted = (state, { payload }) => {
  const {
    serverTime,
    cancellation_policy = { rate: {}, penalties: [] },
  } = payload;

  const { refund_amount: rate = {}, penalties = [] } = cancellation_policy;
  const addDefaultEndDate = ({ end, ...restPenalty }) => ({
    ...restPenalty,
    end: end || state.checkout,
  });
  const { penalty_type: activePenalty } =
    penalties
      .map(addDefaultEndDate)
      .find(({ start, end }) => isActivePenalty({ start, end, serverTime })) ||
    {};
  const refundType = getRefundType(activePenalty);

  return {
    ...state,
    cancellation: {
      ...state.cancellation,
      hasCompletedPrecancellation: false,
      rate,
      fetching: false,
      failed: false,
      refundType,
      penalties,
    },
  };
};

export const requestCancellation = state => ({
  ...state,
  cancellation: {
    ...state.cancellation,
    hasCompletedPrecancellation: true,
    fetching: true,
    showFailedMessage: false,
  },
});

export const requestCancellationFailed = state => ({
  ...state,
  cancellation: {
    ...state.cancellation,
    hasCompletedPrecancellation: true,
    fetching: false,
    showFailedMessage: true,
  },
});

export const requestCancellationCompleted = state => ({
  ...state,
  cancellation: {
    ...state.cancellation,
    hasCompletedPrecancellation: true,
    fetching: false,
    showSuccessMessage: true,
  },
});

export default handleActions(
  {
    [types.RELOAD_BOOKING]: reloadBooking,
    [types.FETCH_BOOKING]: fetchBooking,
    [types.FETCH_BOOKING_COMPLETED]: fetchBookingCompleted,
    [types.FETCH_BOOKING_FAILED]: fetchBookingFailed,
    [types.FETCH_BOOKING_HOTEL_CONTENT_COMPLETED]: fetchBookingHotelContentCompleted,
    [types.FETCH_BOOKING_HOTEL_CONTENT_FAILED]: fetchBookingHotelContentFailed,
    [types.PRECANCELLATION_REQUEST]: requestPrecancellation,
    [types.PRECANCELLATION_REQUEST_FAILED]: requestPrecancellationFailed,
    [types.PRECANCELLATION_REQUEST_COMPLETED]: requestPrecancellationCompleted,
    [types.CANCELLATION_REQUEST]: requestCancellation,
    [types.CANCELLATION_REQUEST_FAILED]: requestCancellationFailed,
    [types.CANCELLATION_REQUEST_COMPLETED]: requestCancellationCompleted,
  },
  initialState
);
